import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, writeBatch, serverTimestamp } from 'firebase/firestore';

const LinkQrPage = () => {
    const [searchParams] = useSearchParams();
    const codeFromUrl = searchParams.get('code') || '';
    const [qrCodeInput, setQrCodeInput] = useState(codeFromUrl.trim().toUpperCase());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { currentUser, userData } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (codeFromUrl) {
            setQrCodeInput(codeFromUrl.trim().toUpperCase());
        }
    }, [codeFromUrl]);

    const handleLinkCode = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        if (!qrCodeInput.trim()) {
            setError('Lütfen QR Kodu giriniz.');
            setLoading(false);
            return;
        }
        if (!currentUser) {
             setError('Giriş yapmalısınız.');
             setLoading(false);
             return;
        }
        
        if (!userData?.isPhoneVerified) {
            setError('QR kod bağlamak için telefon numaranızı doğrulamanız gerekmektedir.');
            setTimeout(() => navigate('/verify-contact'), 2000);
            setLoading(false);
            return;
        }

        const codeToLink = qrCodeInput.trim().toUpperCase().replace(/\/$/, ''); // Normalize input
        const qrDocRef = doc(db, 'qrCodes', codeToLink);
        const profileDocRef = doc(db, 'vehicleProfiles', codeToLink); // Use code as profile ID

        try {
            const qrDocSnap = await getDoc(qrDocRef);

            if (!qrDocSnap.exists()) {
                setError('Geçersiz veya bulunamayan QR Kodu.');
            } else {
                const qrData = qrDocSnap.data();
                if (qrData.isLinked) {
                    // Check if it's linked by the current user already
                    if (qrData.linkedUserId === currentUser.uid) {
                         setError('Bu QR kodu zaten sizin tarafınızdan bağlanmış.');
                    } else {
                        setError('Bu QR Kodu zaten başka bir kullanıcı tarafından bağlanmış.');
                    }
                } else {
                    // Code exists and is not linked, proceed to link
                    const batch = writeBatch(db);

                    // 1. Update qrCodes collection
                    batch.update(qrDocRef, {
                        isLinked: true,
                        linkedUserId: currentUser.uid,
                        linkedAt: serverTimestamp()
                    });

                    // 2. Create vehicleProfiles document
                    batch.set(profileDocRef, {
                        profileId: codeToLink,
                        userId: currentUser.uid,
                        qrCodeId: codeToLink,
                        vehiclePlate: '', // Default empty values
                        visibleNote: 'Kısa süre içinde döneceğim.', // Default note
                        showContactInfoGlobally: true, // Default visible
                        contactInfo: {
                            phone: '',
                            email: '',
                            telegram: '',
                            website: ''
                        },
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp()
                    });

                    await batch.commit();
                    setSuccess(`"${codeToLink}" kodu başarıyla hesabınıza bağlandı!`);
                    setQrCodeInput(''); // Clear input on success
                    // Redirect to the edit page for the newly linked profile
                    setTimeout(() => navigate(`/edit-profile/${codeToLink}`), 1500);
                }
            }
        } catch (err) {
            console.error("Error linking code: ", err);
            setError('Kod bağlanırken bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-10 max-w-lg">
            <div className="bg-white shadow-md rounded-lg p-8">
                <h2 className="text-2xl font-bold mb-6 text-center">QR Kodunu Hesabına Bağla</h2>
                <p className="text-sm text-gray-600 mb-4 text-center">
                    Satın aldığınız sticker üzerindeki benzersiz kodu buraya girin.
                </p>

                {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">{error}</p>}
                {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4 text-sm">{success}</p>}

                <form onSubmit={handleLinkCode}>
                    <div className="mb-6">
                        <label htmlFor="qrCodeInput" className="block text-gray-700 mb-2 font-medium">
                            QR Kodu
                        </label>
                        <input
                            type="text"
                            id="qrCodeInput"
                            value={qrCodeInput}
                            onChange={(e) => setQrCodeInput(e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase" // Force uppercase visually
                            placeholder="Örn: KTABC123"
                            required
                            disabled={loading}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading || success} // Disable after success too
                        className="w-full bg-black text-white py-2.5 rounded hover:bg-gray-800 disabled:opacity-50 transition-colors"
                    >
                        {loading ? 'Bağlanıyor...' : 'Kodu Bağla'}
                    </button>
                </form>
                 <button
                    onClick={() => navigate('/dashboard')}
                    className="mt-4 w-full text-center text-sm text-blue-600 hover:underline"
                >
                    Panele Geri Dön
                </button>
            </div>
        </div>
    );
};

export default LinkQrPage;